import { useTranslation } from 'react-i18next';
import { supportedLanguages, languageNames, type SupportedLanguage } from '@startus/lib';

interface LanguageSwitcherProps {
  className?: string;
  fullWidth?: boolean;
  align?: 'start' | 'end'; // controls dropdown menu alignment relative to trigger
  // 'menuItem' renders the trigger as a full-width sidebar nav item.
  // 'flags' renders flag + full language name instead of the EN/ES diminutive
  // (used by contexts — e.g. the public StartCo form — that want a friendlier,
  // more visible switcher without touching the app-wide nav/sidebar one).
  variant?: 'default' | 'menuItem' | 'flags';
  // Restricts which languages appear, e.g. ['es', 'en'] to drop pt-BR from a
  // specific instance. Defaults to every app-wide supported language, so
  // existing call sites (global nav/sidebar) are unaffected.
  languages?: readonly SupportedLanguage[];
}

// Globe icon — inline to avoid external icon dependency
function GlobeIcon({ className = 'w-4 h-4 shrink-0' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="9" strokeWidth="1.5" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M3.6 9h16.8M3.6 15h16.8M12 3c-3.3 4-3.3 14 0 18M12 3c3.3 4 3.3 14 0 18"
      />
    </svg>
  );
}

function ChevronDown({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

// Simplified flag SVGs — inline for the same reason GlobeIcon is: no
// external icon/flag dependency. Only es/en exist today (flags variant is
// pt-BR-less by design); renderFlag falls back to the globe for any other
// language so a future `languages` list isn't blocked on adding a flag.
function FlagES({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 60 30" aria-hidden="true">
      <rect width="60" height="30" fill="#AA151B" />
      <rect y="7.5" width="60" height="15" fill="#F1BF00" />
    </svg>
  );
}

function FlagUS({ className }: { className: string }) {
  const stripeHeight = 30 / 13;
  const redStripeIndices = [0, 2, 4, 6, 8, 10, 12];
  const cantonWidth = 24;
  const cantonHeight = stripeHeight * 7;
  const starCols = [4, 12, 20];
  const starRows = [cantonHeight / 3, (cantonHeight / 3) * 2];
  return (
    <svg className={className} viewBox="0 0 60 30" aria-hidden="true">
      <rect width="60" height="30" fill="#FFFFFF" />
      {redStripeIndices.map((i) => (
        <rect key={i} y={i * stripeHeight} width="60" height={stripeHeight} fill="#B22234" />
      ))}
      <rect width={cantonWidth} height={cantonHeight} fill="#3C3B6E" />
      {starRows.flatMap((y) =>
        starCols.map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r={0.7} fill="#FFFFFF" />)
      )}
    </svg>
  );
}

function renderFlag(lang: SupportedLanguage, className: string) {
  if (lang === 'es') return <FlagES className={className} />;
  if (lang === 'en') return <FlagUS className={className} />;
  return <GlobeIcon className={className} />;
}

export default function LanguageSwitcher({
  className = '',
  fullWidth = false,
  align = 'end',
  variant = 'default',
  languages = supportedLanguages,
}: LanguageSwitcherProps) {
  const { i18n } = useTranslation();

  const changeLanguage = (lng: SupportedLanguage) => {
    i18n.changeLanguage(lng);
  };

  // Normalize locale tag: prefer full match (e.g. "pt-BR"), fall back to base
  // language (e.g. "es-CR" → "es"), then to the first allowed language if the
  // current one isn't in `languages` at all (e.g. global lang is pt-BR but
  // this instance only offers es/en).
  const normalizeLocale = (locale: string): SupportedLanguage => {
    if (languages.includes(locale as SupportedLanguage)) return locale as SupportedLanguage;
    const base = locale.split('-')[0];
    if (languages.includes(base as SupportedLanguage)) return base as SupportedLanguage;
    return languages[0] ?? 'en';
  };
  const activeLanguage = normalizeLocale(i18n.language ?? 'en');

  const isMenuItem = variant === 'menuItem';
  const isFlags = variant === 'flags';

  return (
    <div
      className={`dropdown ${align === 'end' ? 'dropdown-end' : 'dropdown-start'} ${isMenuItem || fullWidth ? 'w-full' : ''} ${className}`}
    >
      {isFlags ? (
        <div
          tabIndex={0}
          role="button"
          className={`flex items-center gap-2 rounded-lg border border-white/10 bg-[#1E1E1E] px-3 py-2 text-sm font-medium text-white shadow-lg hover:bg-white/5 ${fullWidth ? 'w-full justify-between' : ''}`}
        >
          {renderFlag(activeLanguage, 'h-4 w-6 shrink-0 rounded-[2px] object-cover')}
          <span>{languageNames[activeLanguage]}</span>
          <ChevronDown className="h-3 w-3 opacity-60 shrink-0" />
        </div>
      ) : isMenuItem ? (
        // Matches sidebar nav item style: same padding, icon size, and font as other menu items
        <div
          tabIndex={0}
          role="button"
          className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <GlobeIcon className="w-6 h-6 shrink-0" />
            <span className="font-medium">{activeLanguage.toUpperCase()}</span>
          </div>
          <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
        </div>
      ) : (
        <div
          tabIndex={0}
          role="button"
          className={`btn btn-ghost btn-sm flex items-center gap-4 text-white/70! hover:text-white! ${fullWidth ? 'w-full justify-between' : ''}`}
        >
          <GlobeIcon />
          <span className="font-medium tracking-wide">{activeLanguage.toUpperCase()}</span>
          <ChevronDown className="w-3 h-3 opacity-50 shrink-0" />
        </div>
      )}

      <ul
        tabIndex={0}
        data-theme="dark"
        className={`dropdown-content menu menu-sm rounded-box shadow-lg mt-1 p-1 z-50 ${isFlags ? 'w-44 border border-white/10 bg-[#1E1E1E]' : 'w-44 bg-base-100'}`}
      >
        {languages.map((lng) => (
          <li key={lng}>
            <button
              onClick={() => {
                changeLanguage(lng);
                (document.activeElement as HTMLElement)?.blur();
              }}
              className={`flex items-center gap-2 w-full ${activeLanguage === lng ? 'active' : ''}`}
            >
              {isFlags ? (
                renderFlag(lng, 'h-4 w-6 shrink-0 rounded-[2px] object-cover')
              ) : (
                <span className="text-xs font-bold opacity-50 w-10 shrink-0">
                  {lng.toUpperCase()}
                </span>
              )}
              {languageNames[lng]}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
